import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, User, MessageSquare, AlertCircle, CheckCircle } from 'lucide-react';
import { contactData, pageHeroImages } from '../mock';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await axios.post(`${API_URL}/api/inquiries`, formData);
      setSuccess(true);
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      setTimeout(() => setSuccess(false), 5000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit inquiry. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-container" data-testid="contact-page">
      <section className="page-hero" style={{ backgroundImage: `url(${pageHeroImages.contact})` }}>
        <div className="page-hero-overlay"></div>
        <div className="page-hero-content">
          <h1 className="page-title">Contact Us</h1>
          <p className="page-subtitle">Begin your extraordinary journey</p>
        </div>
      </section>

      <section className="contact-section">
        <div className="section-container">
          <div className="contact-grid">
            {/* Contact Info */}
            <div className="contact-info">
              <h2 className="contact-info-title">Get in Touch</h2>
              <p className="contact-info-text">
                We invite you to reach out for a private consultation. Whether you have a destination in mind or seek inspiration, our team is here to listen and create.
              </p>

              <div className="contact-details">
                <div className="contact-detail-item">
                  <div className="contact-detail-icon">
                    <Mail size={24} />
                  </div>
                  <div>
                    <h3 className="contact-detail-label">Email</h3>
                    <a href={`mailto:${contactData.email}`} className="contact-detail-value">
                      {contactData.email}
                    </a>
                  </div>
                </div>

                <div className="contact-detail-item">
                  <div className="contact-detail-icon">
                    <Phone size={24} />
                  </div>
                  <div>
                    <h3 className="contact-detail-label">Phone</h3>
                    <a href={`tel:${contactData.phone}`} className="contact-detail-value">
                      {contactData.phone}
                    </a>
                  </div>
                </div>

                <div className="contact-detail-item">
                  <div className="contact-detail-icon">
                    <MapPin size={24} />
                  </div>
                  <div>
                    <h3 className="contact-detail-label">Location</h3>
                    <a 
                      href={contactData.googleMapsUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="contact-detail-value contact-address-link"
                    >
                      {contactData.address}
                    </a>
                  </div>
                </div>
              </div>

              <div className="contact-cta-card">
                <h3 className="contact-cta-title">Ready to Begin?</h3>
                <p className="contact-cta-text">
                  Share your vision with us through our journey design form, and we'll start crafting your perfect experience.
                </p>
                <Button onClick={() => navigate('/design-journey')} className="contact-cta-button">
                  Design My Journey
                </Button>
              </div>
            </div>

            {/* Inquiry Form */}
            <div className="inquiry-form-container">
              <h2 className="inquiry-form-title">Send Us a Message</h2>
              <p className="inquiry-form-subtitle">Have a question? We'd love to hear from you.</p>

              {success && (
                <div className="success-banner" data-testid="inquiry-success">
                  <CheckCircle size={20} />
                  <span>Thank you! Your inquiry has been submitted. We'll get back to you soon.</span>
                </div>
              )}

              {error && (
                <div className="error-banner">
                  <AlertCircle size={20} />
                  <span>{error}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="inquiry-form" data-testid="inquiry-form">
                <div className="form-row">
                  <div className="form-group">
                    <label>Your Name *</label>
                    <div className="input-with-icon">
                      <User size={18} />
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Enter your name"
                        required
                        data-testid="inquiry-name"
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Email *</label>
                    <div className="input-with-icon">
                      <Mail size={18} />
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="Enter your email"
                        required
                        data-testid="inquiry-email"
                      />
                    </div>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Phone *</label>
                    <div className="input-with-icon">
                      <Phone size={18} />
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+91 XXXXX XXXXX"
                        required
                        pattern="^\+[0-9]{1,4}[0-9]{6,14}$"
                        title="Please enter phone with country code (e.g., +91 9876543210)"
                        data-testid="inquiry-phone"
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Subject *</label>
                    <div className="input-with-icon">
                      <MessageSquare size={18} />
                      <input
                        type="text"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        placeholder="What's this about?"
                        required
                        data-testid="inquiry-subject"
                      />
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <label>Message *</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us more about your inquiry..."
                    rows={5}
                    required
                    data-testid="inquiry-message"
                  />
                </div>

                <Button type="submit" className="submit-inquiry-btn" disabled={loading} data-testid="inquiry-submit">
                  {loading ? 'Sending...' : (
                    <>
                      <Send size={18} />
                      Send Message
                    </>
                  )}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Google Maps Section */}
      <section className="map-section">
        <div className="map-container">
          <iframe
            src={contactData.googleMapsEmbed}
            width="100%"
            height="450"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Journeys N Tales Location"
          ></iframe>
        </div>
      </section>
    </div>
  );
};

export default Contact;
